import { memo, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Loader2, Zap, Sparkles, Flame, Leaf, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCartStore } from "@/stores/cartStore";
import { toast } from "sonner";
import { ShopifyProduct } from "@/lib/shopify";
import { createInstantCheckout } from "@/lib/checkout";
import { usePrimaryFomoSignal } from "@/hooks/useFomo";
import { SingleFomoSignal } from "@/components/FomoSignals";

// P0 Performance: No framer-motion, simplified DOM, CSS transitions only

interface ProductCardProps {
  product: ShopifyProduct;
  index: number;
  showBestsellerBadge?: boolean;
  showNewBadge?: boolean;
  showHottestBadge?: boolean;
}

// Mini component for FOMO display to avoid hooks in main component conditionals
const FomoDisplay = memo(({ product, isBestseller, isPinned, productIndex }: {
  product: ShopifyProduct;
  isBestseller: boolean;
  isPinned?: boolean;
  productIndex: number;
}) => {
  const fomoSignal = usePrimaryFomoSignal(product, {
    isBestseller,
    isPinned,
    productIndex
  });
  
  if (!fomoSignal) return null;
  
  return <SingleFomoSignal signal={fomoSignal} className="mb-1" />;
});

FomoDisplay.displayName = "FomoDisplay";

export const ProductCard = memo(({ product, index, showBestsellerBadge = false, showNewBadge = false, showHottestBadge = false }: ProductCardProps) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isBuyingNow, setIsBuyingNow] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const addItem = useCartStore(state => state.addItem);
  
  const { node } = product;
  const image = node.images.edges[0]?.node;
  const price = parseFloat(node.priceRange.minVariantPrice.amount);
  const firstVariant = node.variants.edges[0]?.node;
  
  // Determine if product is a bestseller based on tags or index
  const isBestseller = showBestsellerBadge || 
    node.tags?.some((tag: string) => tag.toLowerCase().includes('bestseller') || tag.toLowerCase().includes('popular')) ||
    index < 4; // First 4 products are considered bestsellers
  
  // Get compare at price from variant
  const compareAtPrice = firstVariant?.compareAtPrice?.amount 
    ? parseFloat(firstVariant.compareAtPrice.amount) 
    : null;
  const isOnSale = compareAtPrice && compareAtPrice > price;
  const discountPercent = isOnSale ? Math.round((1 - price / compareAtPrice) * 100) : 0;
  
  // Stock status - simplified
  const allVariants = node.variants.edges;
  const isInStock = allVariants.some(v => v.node.availableForSale);
  
  // Determine product type for badge
  const isVape = node.title.toLowerCase().includes('vape') || (node.title.toLowerCase().includes('magic sauce') && !node.title.toLowerCase().includes('buds'));
  const isHash = node.title.toLowerCase().includes('hash');
  const isBuds = node.title.toLowerCase().includes('buds') || node.title.toLowerCase().includes('flower');

  // Extract strain type from tags or title (Indica, Sativa, Hybrid)
  const strainType = useMemo(() => {
    const tags = node.tags || [];
    const lowerTags = tags.map((t: string) => t.toLowerCase());
    const title = node.title.toLowerCase();
    
    // Known product-to-strain mappings (based on actual product data)
    const strainMappings: Record<string, 'Indica' | 'Sativa' | 'Hybrid'> = {
      'frosty colada': 'Indica',
      'grandaddy': 'Indica',
      'grandaddy purple': 'Indica',
      'gelato': 'Sativa',
      'pineapple': 'Hybrid',
      'pineapple express': 'Hybrid',
      'lemon haze': 'Sativa',
      'lemon cheese': 'Sativa',
      'mango haze': 'Sativa',
      'apple fritter': 'Hybrid',
      'cherry kiss': 'Indica',
      'gorilla cookies': 'Indica',
      'magic sauce': 'Hybrid',
      'bubble hash': 'Indica',
    };
    
    // Check known mappings first
    for (const [productName, strain] of Object.entries(strainMappings)) {
      if (title.includes(productName)) return strain;
    }
    
    // Check tags
    if (lowerTags.some(t => t.includes('indica'))) return 'Indica';
    if (lowerTags.some(t => t.includes('sativa'))) return 'Sativa';
    if (lowerTags.some(t => t.includes('hybrid'))) return 'Hybrid';
    
    // Check title for explicit strain mentions
    if (title.includes('indica')) return 'Indica';
    if (title.includes('sativa')) return 'Sativa';
    if (title.includes('hybrid')) return 'Hybrid';
    
    // Check product images alt text
    const altText = node.images.edges[0]?.node?.altText?.toLowerCase() || '';
    if (altText.includes('indica')) return 'Indica';
    if (altText.includes('sativa')) return 'Sativa';
    if (altText.includes('hybrid')) return 'Hybrid';
    
    // Default based on product type
    if (isVape) return 'Hybrid';
    if (isHash) return 'Indica';
    if (isBuds) return 'Hybrid';
    
    return null;
  }, [node.tags, node.title, node.images.edges, isVape, isHash, isBuds]);

  // Generate scarcity number (deterministic per product for consistency)
  const scarcityCount = useMemo(() => {
    if (!isInStock) return 0;
    // Use product handle hash to generate consistent "random" number 2-8
    const hash = node.handle.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return (hash % 7) + 2; // Returns 2-8
  }, [node.handle, isInStock]);

  const setCartOpen = useCartStore(state => state.setIsOpen);

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!firstVariant || isAdding) return;
    
    setIsAdding(true);
    try {
      await addItem({
        product,
        variantId: firstVariant.id,
        variantTitle: firstVariant.title,
        price: firstVariant.price,
        quantity: 1,
        selectedOptions: firstVariant.selectedOptions || []
      });
      
      setCartOpen(true);
      
      toast.success("Tillagd i varukorgen!", {
        description: node.title,
        position: "top-center"
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleBuyNow = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!firstVariant || isBuyingNow) return;
    
    setIsBuyingNow(true);
    
    // Pre-open a blank tab synchronously (required for mobile popup blocker bypass)
    let preopened: Window | null = null;
    try {
      preopened = window.open("about:blank", "_blank");
    } catch {}

    try {
      const checkoutUrl = await createInstantCheckout({
        variantId: firstVariant.id,
        quantity: 1,
        productId: node.id,
        productName: node.title,
        productHandle: node.handle,
        imageUrl: image?.url || '',
        price: parseFloat(firstVariant.price.amount),
        variantTitle: firstVariant.title,
        currencyCode: firstVariant.price.currencyCode,
      });

      if (!checkoutUrl) {
        preopened?.close();
        toast.error("Kunde inte skapa checkout", {
          description: "Försök igen eller lägg i varukorgen",
          position: "top-center"
        });
        return;
      }

      // Use the pre-opened tab if available
      if (preopened && !preopened.closed) {
        preopened.location.href = checkoutUrl;
        return;
      }

      // Fallback: open new tab
      const opened = window.open(checkoutUrl, "_blank");
      if (opened) return;

      // Final fallback: show toast with action button
      toast.error("Popup blockerades", {
        description: "Klicka på knappen för att öppna kassan",
        position: "top-center",
        duration: 10000,
        action: {
          label: "Öppna kassan",
          onClick: () => window.open(checkoutUrl, "_blank"),
        },
      });
    } catch (err) {
      preopened?.close();
      console.error('Buy now failed:', err);
      toast.error("Kunde inte öppna checkout", {
        description: "Försök igen eller lägg i varukorgen",
        position: "top-center",
      });
    } finally {
      setIsBuyingNow(false);
    }
  };

  return (
    <Link to={`/product/${node.handle}`} className="group block h-full">
      {/* P0: Simplified DOM - reduced nested wrappers for better INP */}
      <article className="relative h-full rounded-2xl bg-card border border-border overflow-hidden transition-all duration-200 group-hover:border-primary/50 group-hover:shadow-xl group-hover:-translate-y-0.5">
        {/* Badge container - simplified, only show most important badge */}
        <div className="absolute top-3 left-3 z-10">
          {isVape && (
            <span className="inline-flex items-center gap-1 bg-accent text-white font-bold px-3 py-1.5 text-xs rounded-full shadow-lg">
              <Zap className="w-3 h-3" />
              VAPE
            </span>
          )}
          {isHash && !isVape && (
            <span className="bg-primary text-primary-foreground font-bold px-3 py-1.5 text-xs rounded-full shadow-lg">
              HASH
            </span>
          )}
          {isBuds && !isVape && !isHash && (
            <span className="inline-flex items-center gap-1 bg-accent/20 text-accent border border-accent/40 font-bold px-3 py-1.5 text-xs rounded-full">
              <Sparkles className="w-3 h-3" />
              BUDS
            </span>
          )}
        </div>
        
        {/* Sale/popularity badge - right side, only one */}
        {(isOnSale || isBestseller || showNewBadge || showHottestBadge) && (
          <div className="absolute top-3 right-3 z-10">
            {isOnSale && discountPercent > 0 ? (
              <span className="bg-destructive text-destructive-foreground font-bold px-3 py-1.5 text-sm rounded-full shadow-lg">
                -{discountPercent}%
              </span>
            ) : showHottestBadge ? (
              <span className="inline-flex items-center gap-1 bg-gradient-to-r from-cta to-destructive text-white font-bold px-2.5 py-1 text-xs rounded-full shadow-lg">
                <Flame className="w-3 h-3" />
                Hetast
              </span>
            ) : showNewBadge ? (
              <span className="inline-flex items-center gap-1 bg-accent text-white font-bold px-2.5 py-1 text-xs rounded-full shadow-lg">
                <Sparkles className="w-3 h-3" />
                Ny
              </span>
            ) : isBestseller ? (
              <span className="inline-flex items-center gap-1 bg-cta text-cta-foreground font-bold px-2.5 py-1 text-xs rounded-full shadow-lg">
                <Flame className="w-3 h-3" />
                Populär
              </span>
            ) : null}
          </div>
        )}

        {/* Image - CLEAN, NO OVERLAYS */}
        <div 
          className="relative aspect-[4/5] sm:aspect-square overflow-hidden"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {image ? (
            <img
              src={`${image.url}&width=400`}
              srcSet={`${image.url}&width=300 300w, ${image.url}&width=400 400w, ${image.url}&width=600 600w`}
              alt={image.altText || `${node.title} – Köp hos Vapebolag`}
              className="relative w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading={index < 4 ? "eager" : "lazy"}
              decoding={index < 4 ? "sync" : "async"}
              width="400"
              height="500"
              sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 25vw"
              fetchPriority={index < 2 ? "high" : "auto"}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-7xl bg-card">
              💨
            </div>
          )}
          
          {/* Add to cart overlay on hover - subtle, no dark blur */}
          {isHovered && firstVariant?.availableForSale && (
            <div
              className="absolute inset-0 bg-black/30 flex items-center justify-center z-20 animate-fade-in"
              onClick={handleAddToCart}
            >
              <Button
                size="lg"
                disabled={isAdding}
                className="cta-gradient font-bold px-6 py-3 rounded-xl shadow-2xl"
              >
                {isAdding ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Lägg i varukorg
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        <div className="p-3 sm:p-5">
          {/* Strain type badge (Indica/Hybrid/Sativa) - Snozy-style colored badges */}
          {strainType && (
            <div className="mb-2 sm:mb-2.5">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-sm ${
                strainType === 'Indica' 
                  ? 'bg-purple-600 text-white' 
                  : strainType === 'Sativa' 
                    ? 'bg-amber-500 text-white' 
                    : 'bg-emerald-600 text-white'
              }`}>
                <Leaf className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                {strainType}
              </span>
            </div>
          )}
          
          {/* Title: 3 lines on mobile, 2 on desktop */}
          <h3 className="font-bold text-foreground line-clamp-3 sm:line-clamp-2 mb-2 sm:mb-3 group-hover:text-primary transition-colors text-sm sm:text-lg leading-tight sm:leading-snug min-h-[3.5rem] sm:min-h-[3rem]">
            {node.title}
          </h3>
          
          {/* Price */}
          <div className="flex items-baseline gap-1.5 sm:gap-2 mb-1 sm:mb-2">
            <span className="text-lg sm:text-2xl font-extrabold text-gradient">
              {price.toFixed(0)} kr
            </span>
            {isOnSale && compareAtPrice && (
              <span className="text-xs sm:text-sm text-muted-foreground line-through">
                {compareAtPrice.toFixed(0)} kr
              </span>
            )}
          </div>
          
          {/* Truthful FOMO signal - only shows for bestsellers/featured/pinned */}
          <FomoDisplay 
            product={product} 
            isBestseller={isBestseller} 
            isPinned={showHottestBadge} 
            productIndex={index}
          />
          
          {/* Buy Now button - always goes to checkout */}
          <div onClick={(e) => e.stopPropagation()} className="mt-2 sm:mt-3">
            <Button
              size="sm"
              onClick={handleBuyNow}
              disabled={isBuyingNow || !firstVariant?.availableForSale}
              className="w-full cta-gradient font-bold h-10 sm:h-11 rounded-xl text-xs sm:text-sm shadow-lg hover:shadow-xl hover:shadow-primary/20 transition-all"
            >
              {isBuyingNow ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                  Köp nu
                </>
              )}
            </Button>
          </div>

          {/* Stock status with scarcity */}
          {!isInStock && (
            <p className="text-xs sm:text-sm text-destructive font-medium mt-1.5 sm:mt-2">Slutsåld</p>
          )}
          {isInStock && scarcityCount <= 5 && (
            <p className="text-[10px] sm:text-xs text-cta mt-1.5 sm:mt-2 flex items-center gap-1 font-medium">
              <AlertTriangle className="w-3 h-3" />
              Endast {scarcityCount} kvar i lager
            </p>
          )}
          {isInStock && scarcityCount > 5 && (
            <p className="text-[10px] sm:text-xs text-accent mt-1.5 sm:mt-2">✓ I lager</p>
          )}
        </div>
      </article>
    </Link>
  );
});

ProductCard.displayName = "ProductCard";
export default ProductCard;
